import styled, { css } from 'styled-components';

import { CFG_ANIM, CFG_CHROME } from '../../../../theme/configurator-tokens';
import { PHONE_STRIP_H } from '../../steps/finish/components/DevicePreview/frame-art';

/**
 * Stacking levels for the host layer, as rungs on the wizard's ladder:
 * WizardLayout pane 10 < ProgressHeader / WizardFooter 20 < UserMenu 40.
 *
 * - `resting`: under everything. The build steps sit on transparent panes, so the
 *   devices show through and the chrome paints on top.
 * - `elevated`: over the step panes and chrome bars, for a surface whose preview sits
 *   on an OPAQUE panel (the payment-success screen). It deliberately stops BELOW the
 *   UserMenu, whose popover must still open over the devices.
 * - `fullscreen`: over everything, including the UserMenu, for the Live Preview overlay
 *   (its backdrop portals in here and is meant to cover the whole wizard).
 */
export const STAGE_Z = { resting: 0, elevated: 30, fullscreen: 60 } as const;

/**
 * The persistent host layer. A viewport-fixed, click-through plane that holds the
 * warm device wrappers (mac, phone, morph) for the whole desktop session. It sits BELOW the step
 * chrome (earlier in the DOM, so the transparent step panes let the devices show
 * through and the logo/switcher/pill paint on top) until a surface lifts it, per
 * {@link STAGE_Z}.
 */
export const Layer = styled.div<{ $z: number }>`
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: ${({ $z }) => $z};
`;

/**
 * Outer layer of one device's positioned box. Its position (left/top/width/height)
 * is applied INSTANTLY so the device tracks its measured anchor without ever
 * animating size/position — no scale, and no flicker from the host's settle-measure
 * burst. The only thing that animates is the entrance / exit SLIDE + FADE (transform
 * + opacity), and only while `$animate` is set: crossing surfaces (step → step,
 * build → finish, opening the full-screen preview) slides the device out to the left
 * + fades it, and slides the next one in from the right + fades it. The bezel +
 * iframe inside fill it, so they always travel together (no seam to desync).
 */
export const StepFade = styled.div<{ $interactive: boolean; $z: number; $animate: boolean }>`
  position: absolute;
  pointer-events: ${({ $interactive }) => ($interactive ? 'auto' : 'none')};
  z-index: ${({ $z }) => $z};
  ${({ $animate }) =>
    $animate
      ? css`
          transition:
            transform ${CFG_ANIM.STEP_SLIDE_MS}ms ${CFG_ANIM.EASE},
            opacity ${CFG_ANIM.STEP_SLIDE_MS}ms ${CFG_ANIM.EASE};
        `
      : css`
          transition: none;
        `}
  will-change: transform, opacity;
`;

/**
 * Inner layer carrying the device-switcher cross-SLIDE. Within a single step, toggling
 * Mobile / Desktop parks the inactive device off-axis (translate + fade) and slides
 * the chosen one in — the premium switch motion the user wants kept. `$slide` is on
 * ONLY while staying in single mode (a step/surface change snaps it so the outer
 * StepFade owns that transition as a fade, never a slide).
 */
export const ParkSlide = styled.div<{ $parked: boolean; $parkDir: number; $slide: boolean }>`
  position: absolute;
  inset: 0;
  transform: translate3d(
    ${({ $parked, $parkDir }) => ($parked ? $parkDir * CFG_ANIM.DEVICE_SWITCH_OFFSET_PX : 0)}px,
    0,
    0
  );
  opacity: ${({ $parked }) => ($parked ? 0 : 1)};
  ${({ $slide }) =>
    $slide
      ? css`
          transition:
            transform ${CFG_ANIM.DEVICE_SWITCH_MS}ms ${CFG_ANIM.EASE_DEVICE_SWITCH},
            opacity ${CFG_ANIM.DEVICE_SWITCH_MS}ms ${CFG_ANIM.EASE_DEVICE_SWITCH};
        `
      : css`
          transition: none;
        `}
  will-change: transform, opacity;
`;

/* ----------------------------------------------------------------------------- *
 * MacBook bezel (ported from the finish DevicePreview MacbookFrame, with the
 * iframe parent kept invariant so device/fullscreen swaps never re-parent it).
 * ----------------------------------------------------------------------------- */

export const MacRoot = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

export const MacScreen = styled.div<{
  $bg?: string;
  $left: string;
  $top: string;
  $width: string;
  $height: string;
  $bare: boolean;
}>`
  position: absolute;
  ${({ $bare, $left, $top, $width, $height }) =>
    $bare
      ? css`
          inset: 0;
        `
      : css`
          left: ${$left};
          top: ${$top};
          width: ${$width};
          height: ${$height};
        `}
  display: flex;
  flex-direction: column;
  overflow: hidden;
  /* Own stacking context so the loading placeholder can't escape above the PNG. */
  isolation: isolate;
  background: ${({ $bg, $bare }) => (($bare ? 'transparent' : $bg) ?? CFG_CHROME.previewScreen)};
`;

export const MacTopBar = styled.div<{ $color?: string; $bare: boolean }>`
  flex-shrink: 0;
  height: ${({ $bare }) => ($bare ? '0' : '6%')};
  background: ${({ $color }) => $color ?? 'transparent'};
`;

export const FrameContent = styled.div`
  position: relative;
  min-height: 0;
  flex: 1;
  overflow: hidden;
`;

// Sandbox the cross-origin preview (omit allow-top-navigation so a framed site can't
// redirect the studio tab); the URL is already scheme-validated by buildPreviewUrl.
export const ScaledIframe = styled.iframe.attrs({
  sandbox: 'allow-scripts allow-same-origin allow-forms allow-popups',
})<{ $width: number; $height: number; $scale: number; $shown: boolean }>`
  transform-origin: top left;
  border: 0;
  width: ${({ $width }) => $width}px;
  height: ${({ $height }) => $height}px;
  transform: scale(${({ $scale }) => $scale});
  opacity: ${({ $shown }) => ($shown ? 1 : 0)};
  transition: opacity ${CFG_ANIM.PREVIEW_FADE_MS}ms ease;
`;

export const FrameOverlayImg = styled.img<{ $hidden: boolean }>`
  pointer-events: none;
  position: absolute;
  inset: 0;
  height: 100%;
  width: 100%;
  user-select: none;
  opacity: ${({ $hidden }) => ($hidden ? 0 : 1)};
`;

/* ----------------------------------------------------------------------------- *
 * iPhone bezel (ported from the finish DevicePreview PhoneFrame; ALWAYS wraps the
 * iframe in the Zoom layer so toggling 1:1 ↔ zoomed never re-parents the iframe).
 * ----------------------------------------------------------------------------- */

export const PhoneRoot = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

/** Screen region of the phone bezel. The inset arrives as props from
 *  `PHONE_FRAME_DEFAULT.screen` (frame-art.ts) rather than being restated here, so the
 *  bezelled phone and the unified morph device can never drift apart. */
export const PhoneScreen = styled.div<{
  $radius: number;
  $bg?: string;
  $bare: boolean;
  $left: string;
  $top: string;
  $width: string;
  $height: string;
}>`
  position: absolute;
  ${({ $bare, $left, $top, $width, $height }) =>
    $bare
      ? css`
          inset: 0;
        `
      : css`
          left: ${$left};
          top: ${$top};
          width: ${$width};
          height: ${$height};
        `}
  display: flex;
  flex-direction: column;
  overflow: hidden;
  isolation: isolate;
  background: ${({ $bg, $bare }) => (($bare ? 'transparent' : $bg) ?? CFG_CHROME.phoneScreen)};
  border-radius: ${({ $radius, $bare }) => ($bare ? 0 : $radius)}px;
`;

export const PhoneTopBar = styled.div<{ $color?: string; $bare: boolean }>`
  flex-shrink: 0;
  height: ${({ $bare }) => ($bare ? '0' : PHONE_STRIP_H.top)};
  background: ${({ $color }) => $color ?? 'transparent'};
`;

export const PhoneBottomBar = styled.div<{ $color?: string; $bare: boolean }>`
  flex-shrink: 0;
  height: ${({ $bare }) => ($bare ? '0' : PHONE_STRIP_H.bottom)};
  background: ${({ $color }) => $color ?? 'transparent'};
  transition: background-color ${CFG_ANIM.PREVIEW_FADE_MS}ms ease;
`;

/**
 * The virtual-viewport layer: sized in px to the width the framed site should BELIEVE it
 * has (420 mobile / 1280 desktop) and scaled down to the physical screen cut-out. The
 * iframe inside is a plain 100% x 100% {@link FillIframe}, so the cross-origin document's
 * own box never carries a computed number — only this wrapper does. Shared by the phone
 * and the unified morph device; both always render it (even at 1:1), so toggling zoom or
 * switching device can never re-parent the iframe and reload it.
 */
export const ZoomLayer = styled.div<{ $width: number; $height: string; $scale: number }>`
  position: absolute;
  left: 0;
  top: 0;
  transform-origin: top left;
  width: ${({ $width }) => $width}px;
  height: ${({ $height }) => $height};
  transform: scale(${({ $scale }) => $scale});
`;

// Sandbox the cross-origin preview (omit allow-top-navigation); URL is scheme-validated.
export const FillIframe = styled.iframe.attrs({
  sandbox: 'allow-scripts allow-same-origin allow-forms allow-popups',
})<{ $shown: boolean }>`
  height: 100%;
  width: 100%;
  border: 0;
  opacity: ${({ $shown }) => ($shown ? 1 : 0)};
  transition: opacity ${CFG_ANIM.PREVIEW_FADE_MS}ms ease;
`;

/* ----------------------------------------------------------------------------- *
 * Unified single-mode morph device: ONE warm iframe whose bezel + virtual width
 * morph between the laptop and phone on a Mobile / Desktop switch, so the same
 * contentWindow (in-preview route/scroll) survives. The iframe lives in a fixed
 * 3-child screen layout (top strip / content / bottom strip) so a toggle only
 * rewrites CSS and never re-parents it.
 * ----------------------------------------------------------------------------- */

export const MorphRoot = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

/** Screen region positioned per the ACTIVE bezel's inset (percent props); a flex
 *  column so the morphing chrome strips + content stack exactly like Mac/Phone.
 *  `$bare` (the full-screen Live Preview, which reuses this same device): fills the
 *  wrapper edge-to-edge with no bezel-relative inset, same as Mac/PhoneScreen's own
 *  `$bare`. */
export const MorphScreen = styled.div<{
  $bg?: string;
  $left: string;
  $top: string;
  $width: string;
  $height: string;
  $radius: number;
  $bare: boolean;
}>`
  position: absolute;
  ${({ $bare, $left, $top, $width, $height }) =>
    $bare
      ? css`
          inset: 0;
        `
      : css`
          left: ${$left};
          top: ${$top};
          width: ${$width};
          height: ${$height};
        `}
  display: flex;
  flex-direction: column;
  overflow: hidden;
  isolation: isolate;
  border-radius: ${({ $radius, $bare }) => ($bare ? 0 : $radius)}px;
  background: ${({ $bg, $bare }) => (($bare ? 'transparent' : $bg) ?? CFG_CHROME.previewScreen)};
`;

/** Status-bar / browser-chrome strip; height morphs per device (0 collapses it),
 *  also 0 when `$bare` (full-screen has no status-bar chrome of its own). */
export const MorphTopBar = styled.div<{ $color?: string; $h: string; $bare: boolean }>`
  flex-shrink: 0;
  height: ${({ $h, $bare }) => ($bare ? '0' : $h)};
  background: ${({ $color }) => $color ?? 'transparent'};
`;

/** Home-indicator strip (phone only; height 0 on desktop, and 0 when `$bare`). Tint
 *  animates like the phone device's home strip once the preview reveals. */
export const MorphBottomBar = styled.div<{ $color?: string; $h: string; $bare: boolean }>`
  flex-shrink: 0;
  height: ${({ $h, $bare }) => ($bare ? '0' : $h)};
  background: ${({ $color }) => $color ?? 'transparent'};
  transition: background-color ${CFG_ANIM.PREVIEW_FADE_MS}ms ease;
`;

/** One bezel artwork layered over the morph screen. Both frames stay mounted (so the
 *  laptop PNG is never re-decoded) and the inactive one is simply hidden — with NO
 *  transition: on a device switch the whole device travels behind {@link SwitchSlide},
 *  and the outgoing frame is carried out by the ghost, so a crossfade here would only
 *  show the old bezel on the incoming device. Mirrors FrameOverlayImg placement. */
export const BezelImg = styled.img<{ $shown: boolean }>`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  pointer-events: none;
  user-select: none;
  opacity: ${({ $shown }) => ($shown ? 1 : 0)};
`;

/**
 * Inner layer carrying the unified morph device's Mobile / Desktop cross-slide — the
 * same motion the two-device {@link ParkSlide} used to give, on the one warm iframe.
 * A switch parks the incoming device off-axis (no transition), then animates it to
 * rest while the outgoing device's GHOST (bezel + brand fill, see DeviceGhost) travels
 * the opposite way. Same tokens as ParkSlide, so the switch feels unchanged.
 */
export const SwitchSlide = styled.div<{ $tx: number; $opacity: number; $animate: boolean }>`
  position: absolute;
  inset: 0;
  transform: translate3d(${({ $tx }) => $tx}px, 0, 0);
  opacity: ${({ $opacity }) => $opacity};
  ${({ $animate }) =>
    $animate
      ? css`
          transition:
            transform ${CFG_ANIM.DEVICE_SWITCH_MS}ms ${CFG_ANIM.EASE_DEVICE_SWITCH},
            opacity ${CFG_ANIM.DEVICE_SWITCH_MS}ms ${CFG_ANIM.EASE_DEVICE_SWITCH};
        `
      : css`
          transition: none;
        `}
  will-change: transform, opacity;
`;
